// src/pages/settings/QuoteTemplates.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  ColorPicker
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as DefaultIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { SketchPicker } from 'react-color';

// Components
import MagicCard from '../../components/ui/MagicCard';
import MagicButton from '../../components/ui/MagicButton';
import MagicTextField from '../../components/ui/FormFields/MagicTextField';
import MagicLoading from '../../components/ui/MagicLoading';

// Services
import { api } from '../../services/api';

const QuoteTemplates = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isDefault: false,
    companyInfo: {
      useSystemDefaults: true,
      logo: {
        show: true,
        height: 60
      },
      fields: {
        name: { visible: true, value: '' },
        address: { visible: true, value: '' },
        phone: { visible: true, value: '' },
        email: { visible: true, value: '' },
        website: { visible: true, value: '' },
        registrationNumber: { visible: false, value: '' },
        taxId: { visible: false, value: '' }
      }
    },
    sections: [
      { key: 'header', name: 'Header', visible: true, order: 1 },
      { key: 'clientInfo', name: 'Client Information', visible: true, order: 2 },
      { key: 'quoteInfo', name: 'Quote Information', visible: true, order: 3 },
      { key: 'servicesTable', name: 'Services Table', visible: true, order: 4 },
      { key: 'pricingSummary', name: 'Pricing Summary', visible: true, order: 5 },
      { key: 'terms', name: 'Terms & Conditions', visible: true, order: 6 },
      { key: 'signatures', name: 'Signature Section', visible: false, order: 7 },
      { key: 'footer', name: 'Footer', visible: true, order: 8 }
    ],
    fieldConfig: {
      client: {
        showCompanyName: true,
        showContactPerson: true,
        showEmail: true,
        showPhone: true,
        showAddress: true
      },
      quote: {
        showQuoteNumber: true,
        showDate: true,
        showValidUntil: true,
        showPreparedBy: true,
        showProjectName: false,
        showReference: false
      },
      services: {
        showCategory: true,
        showDescription: false,
        showQuantity: true,
        showUnitPrice: true,
        showTotal: true,
        showEstimatedHours: false,
        groupByCategory: false
      },
      pricing: {
        showSubtotal: true,
        showFactors: true,
        showDiscounts: true,
        showTax: false,
        taxLabel: 'Tax',
        taxRate: 0,
        showTotal: true,
        showCurrency: true
      }
    },
    styles: {
      primaryColor: '#1976d2',
      secondaryColor: '#424242',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontSize: {
        base: 12,
        heading: 24,
        subheading: 18
      }
    },
    pageConfig: {
      size: 'A4',
      orientation: 'portrait',
      margins: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 20,
        unit: 'mm'
      },
      showPageNumbers: true,
      pageNumberPosition: 'bottom-center'
    },
    watermark: {
      enabled: false,
      text: 'CONFIDENTIAL',
      opacity: 0.1
    },
    customTemplate: {
      useCustom: false,
      html: '',
      css: ''
    }
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/pricing/templates');
      if (response?.data?.templates) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      enqueueSnackbar('Failed to load templates', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      description: '',
      isDefault: false,
      companyInfo: {
        useSystemDefaults: true,
        logo: { show: true, height: 60 },
        fields: {
          name: { visible: true, value: '' },
          address: { visible: true, value: '' },
          phone: { visible: true, value: '' },
          email: { visible: true, value: '' },
          website: { visible: true, value: '' },
          registrationNumber: { visible: false, value: '' },
          taxId: { visible: false, value: '' }
        }
      },
      sections: [
        { key: 'header', name: 'Header', visible: true, order: 1 },
        { key: 'clientInfo', name: 'Client Information', visible: true, order: 2 },
        { key: 'quoteInfo', name: 'Quote Information', visible: true, order: 3 },
        { key: 'servicesTable', name: 'Services Table', visible: true, order: 4 },
        { key: 'pricingSummary', name: 'Pricing Summary', visible: true, order: 5 },
        { key: 'terms', name: 'Terms & Conditions', visible: true, order: 6 },
        { key: 'signatures', name: 'Signature Section', visible: false, order: 7 },
        { key: 'footer', name: 'Footer', visible: true, order: 8 }
      ],
      fieldConfig: {
        client: {
          showCompanyName: true,
          showContactPerson: true,
          showEmail: true,
          showPhone: true,
          showAddress: true
        },
        quote: {
          showQuoteNumber: true,
          showDate: true,
          showValidUntil: true,
          showPreparedBy: true,
          showProjectName: false,
          showReference: false
        },
        services: {
          showCategory: true,
          showDescription: false,
          showQuantity: true,
          showUnitPrice: true,
          showTotal: true,
          showEstimatedHours: false,
          groupByCategory: false
        },
        pricing: {
          showSubtotal: true,
          showFactors: true,
          showDiscounts: true,
          showTax: false,
          taxLabel: 'Tax',
          taxRate: 0,
          showTotal: true,
          showCurrency: true
        }
      },
      styles: {
        primaryColor: '#1976d2',
        secondaryColor: '#424242',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: { base: 12, heading: 24, subheading: 18 }
      },
      pageConfig: {
        size: 'A4',
        orientation: 'portrait',
        margins: { top: 20, right: 20, bottom: 20, left: 20, unit: 'mm' },
        showPageNumbers: true,
        pageNumberPosition: 'bottom-center'
      },
      watermark: { enabled: false, text: 'CONFIDENTIAL', opacity: 0.1 },
      customTemplate: { useCustom: false, html: '', css: '' }
    });
    setActiveTab(0);
    setDialogOpen(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData(template);
    setActiveTab(0);
    setDialogOpen(true);
  };

  const handleDuplicate = async (template) => {
    try {
      const response = await api.post(`/pricing/templates/${template._id}/duplicate`);
      if (response?.data?.template) {
        enqueueSnackbar('Template duplicated successfully', { variant: 'success' });
        loadTemplates();
      }
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      enqueueSnackbar('Failed to duplicate template', { variant: 'error' });
    }
  };

  const handleDelete = (template) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/pricing/templates/${selectedTemplate._id}`);
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      enqueueSnackbar('Failed to delete template', { variant: 'error' });
    }
  };

  const handleSave = async () => {
    try {
      if (selectedTemplate) {
        await api.put(`/pricing/templates/${selectedTemplate._id}`, formData);
        enqueueSnackbar('Template updated successfully', { variant: 'success' });
      } else {
        await api.post('/pricing/templates', formData);
        enqueueSnackbar('Template created successfully', { variant: 'success' });
      }
      setDialogOpen(false);
      loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      enqueueSnackbar('Failed to save template', { variant: 'error' });
    }
  };

  const handlePreview = async (template) => {
    try {
      const response = await api.post(`/pricing/templates/${template._id}/preview`);
      if (response?.data?.previewUrl) {
        window.open(response.data.previewUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      enqueueSnackbar('Failed to generate preview', { variant: 'error' });
    }
  };

  const handleFieldChange = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  if (loading) {
    return <MagicLoading fullScreen message="Loading templates..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Quote Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage PDF templates for your quotes
          </Typography>
        </Box>
        
        <MagicButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Template
        </MagicButton>
      </Box>

      {/* Templates Table */}
      <MagicCard>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Template Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Modified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        No templates found. Create your first template.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template._id}>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {template.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{template.description}</TableCell>
                    <TableCell>
                      {template.isDefault && (
                        <Chip 
                          icon={<DefaultIcon />} 
                          label="Default" 
                          size="small" 
                          color="primary" 
                        />
                      )}
                      {template.isActive && (
                        <Chip 
                          label="Active" 
                          size="small" 
                          color="success" 
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(template.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        onClick={() => handlePreview(template)}
                        title="Preview"
                      >
                        <PreviewIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEdit(template)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDuplicate(template)}
                        title="Duplicate"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(template)}
                        title="Delete"
                        disabled={template.isDefault}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MagicCard>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
              <Tab label="Basic Info" icon={<SettingsIcon />} />
              <Tab label="Sections & Fields" icon={<DescriptionIcon />} />
              <Tab label="Styling" icon={<PaletteIcon />} />
              <Tab label="Custom HTML" icon={<CodeIcon />} />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          {activeTab === 0 && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MagicTextField
                    fullWidth
                    label="Template Name"
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <MagicTextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isDefault}
                        onChange={(e) => handleFieldChange('isDefault', e.target.checked)}
                      />
                    }
                    label="Set as default template"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.companyInfo.useSystemDefaults}
                        onChange={(e) => handleFieldChange('companyInfo.useSystemDefaults', e.target.checked)}
                      />
                    }
                    label="Use system company settings"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Page Configuration</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Page Size</InputLabel>
                        <Select
                          value={formData.pageConfig.size}
                          onChange={(e) => handleFieldChange('pageConfig.size', e.target.value)}
                          label="Page Size"
                        >
                          <MenuItem value="A4">A4</MenuItem>
                          <MenuItem value="Letter">Letter</MenuItem>
                          <MenuItem value="Legal">Legal</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Orientation</InputLabel>
                        <Select
                          value={formData.pageConfig.orientation}
                          onChange={(e) => handleFieldChange('pageConfig.orientation', e.target.value)}
                          label="Orientation"
                        >
                          <MenuItem value="portrait">Portrait</MenuItem>
                          <MenuItem value="landscape">Landscape</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Configure which sections appear in the template
              </Typography>
              {formData.sections.map((section, index) => {
                // Map section keys to readable names
                const sectionNames = {
                  header: 'Header',
                  clientInfo: 'Client Information',
                  quoteInfo: 'Quote Information',
                  servicesTable: 'Services Table',
                  pricingSummary: 'Pricing Summary',
                  terms: 'Terms & Conditions',
                  signatures: 'Signature Section',
                  footer: 'Footer'
                };
                const sectionName = section.name || sectionNames[section.key] || section.key;
                
                // Define fields for each section
                const sectionFields = {
                  clientInfo: {
                    showCompanyName: 'Company Name',
                    showContactPerson: 'Contact Person',
                    showEmail: 'Email',
                    showPhone: 'Phone',
                    showAddress: 'Address'
                  },
                  quoteInfo: {
                    showQuoteNumber: 'Quote Number',
                    showDate: 'Date',
                    showValidUntil: 'Valid Until',
                    showPreparedBy: 'Prepared By',
                    showProjectName: 'Project Name',
                    showReference: 'Reference Number'
                  },
                  servicesTable: {
                    showCategory: 'Category',
                    showDescription: 'Description',
                    showQuantity: 'Quantity',
                    showUnitPrice: 'Unit Price',
                    showTotal: 'Total',
                    showEstimatedHours: 'Estimated Hours',
                    groupByCategory: 'Group by Category'
                  },
                  pricingSummary: {
                    showSubtotal: 'Subtotal',
                    showFactors: 'Factor Adjustments',
                    showDiscounts: 'Discounts',
                    showTax: 'Tax',
                    showTotal: 'Total',
                    showCurrency: 'Currency'
                  }
                };
                
                // Map section key to fieldConfig key
                const fieldConfigKey = {
                  clientInfo: 'client',
                  quoteInfo: 'quote',
                  servicesTable: 'services',
                  pricingSummary: 'pricing'
                };
                
                const hasFields = sectionFields[section.key];
                const configKey = fieldConfigKey[section.key];
                
                return (
                <Accordion key={section.key}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={section.visible}
                          onChange={(e) => {
                            const newSections = [...formData.sections];
                            newSections[index].visible = e.target.checked;
                            handleFieldChange('sections', newSections);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label={sectionName}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {!hasFields ? (
                        <Typography variant="body2" color="text.secondary">
                          This section has no configurable fields
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Configure which fields appear in this section:
                          </Typography>
                          <Grid container spacing={2}>
                            {Object.entries(sectionFields[section.key]).map(([field, label]) => (
                              <Grid item xs={12} sm={6} key={field}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={formData.fieldConfig?.[configKey]?.[field] ?? 
                                        (configKey === 'services' ? false : true)}
                                      onChange={(e) => 
                                        handleFieldChange(`fieldConfig.${configKey}.${field}`, e.target.checked)
                                      }
                                      disabled={!section.visible}
                                    />
                                  }
                                  label={label}
                                />
                              </Grid>
                            ))}
                          </Grid>
                          
                          {/* Special handling for tax fields in pricing section */}
                          {section.key === 'pricingSummary' && formData.fieldConfig?.pricing?.showTax && (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="Tax Label"
                                  size="small"
                                  value={formData.fieldConfig?.pricing?.taxLabel || 'Tax'}
                                  onChange={(e) => handleFieldChange('fieldConfig.pricing.taxLabel', e.target.value)}
                                  disabled={!section.visible}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="Tax Rate (%)"
                                  type="number"
                                  size="small"
                                  value={formData.fieldConfig?.pricing?.taxRate || 0}
                                  onChange={(e) => handleFieldChange('fieldConfig.pricing.taxRate', parseFloat(e.target.value))}
                                  disabled={!section.visible}
                                />
                              </Grid>
                            </Grid>
                          )}
                        </>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
                );
              })}
            </Box>
          )}


          {activeTab === 2 && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>Primary Color</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      size="small"
                      value={formData.styles.primaryColor}
                      onChange={(e) => handleFieldChange('styles.primaryColor', e.target.value)}
                      inputProps={{ style: { width: 100 } }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: formData.styles.primaryColor,
                        border: '1px solid #ccc',
                        borderRadius: 1
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>Secondary Color</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      size="small"
                      value={formData.styles.secondaryColor}
                      onChange={(e) => handleFieldChange('styles.secondaryColor', e.target.value)}
                      inputProps={{ style: { width: 100 } }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: formData.styles.secondaryColor,
                        border: '1px solid #ccc',
                        borderRadius: 1
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Font Family</InputLabel>
                    <Select
                      value={formData.styles.fontFamily}
                      onChange={(e) => handleFieldChange('styles.fontFamily', e.target.value)}
                      label="Font Family"
                    >
                      <MenuItem value="Helvetica, Arial, sans-serif">Helvetica</MenuItem>
                      <MenuItem value="Arial, sans-serif">Arial</MenuItem>
                      <MenuItem value="'Times New Roman', serif">Times New Roman</MenuItem>
                      <MenuItem value="Georgia, serif">Georgia</MenuItem>
                      <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Font Sizes</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Base Size"
                        type="number"
                        size="small"
                        value={formData.styles.fontSize.base}
                        onChange={(e) => handleFieldChange('styles.fontSize.base', parseInt(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Heading Size"
                        type="number"
                        size="small"
                        value={formData.styles.fontSize.heading}
                        onChange={(e) => handleFieldChange('styles.fontSize.heading', parseInt(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Subheading Size"
                        type="number"
                        size="small"
                        value={formData.styles.fontSize.subheading}
                        onChange={(e) => handleFieldChange('styles.fontSize.subheading', parseInt(e.target.value))}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.watermark.enabled}
                        onChange={(e) => handleFieldChange('watermark.enabled', e.target.checked)}
                      />
                    }
                    label="Enable Watermark"
                  />
                  {formData.watermark.enabled && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          label="Watermark Text"
                          size="small"
                          value={formData.watermark.text}
                          onChange={(e) => handleFieldChange('watermark.text', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Opacity"
                          type="number"
                          size="small"
                          value={formData.watermark.opacity}
                          onChange={(e) => handleFieldChange('watermark.opacity', parseFloat(e.target.value))}
                          inputProps={{ min: 0, max: 1, step: 0.1 }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 3 && (
            <Box sx={{ pt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.customTemplate.useCustom}
                    onChange={(e) => handleFieldChange('customTemplate.useCustom', e.target.checked)}
                  />
                }
                label="Use custom HTML template"
              />
              {formData.customTemplate.useCustom && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You can use Handlebars templating syntax. Available variables: quote, company, template
                  </Alert>
                  <TextField
                    fullWidth
                    label="HTML Template"
                    multiline
                    rows={10}
                    value={formData.customTemplate.html}
                    onChange={(e) => handleFieldChange('customTemplate.html', e.target.value)}
                    sx={{ mb: 2 }}
                    placeholder="<html>...</html>"
                  />
                  <TextField
                    fullWidth
                    label="Custom CSS"
                    multiline
                    rows={5}
                    value={formData.customTemplate.css}
                    onChange={(e) => handleFieldChange('customTemplate.css', e.target.value)}
                    placeholder="body { font-family: Arial; }"
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MagicButton onClick={() => setDialogOpen(false)}>
            Cancel
          </MagicButton>
          <MagicButton variant="contained" onClick={handleSave}>
            {selectedTemplate ? 'Update' : 'Create'}
          </MagicButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{selectedTemplate?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MagicButton onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </MagicButton>
          <MagicButton color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </MagicButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteTemplates;